import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/context/SessionContextProvider';
import { setDynamicSlashItems } from '../components/contract-editor/extensions/slashCommandSuggestion';
import type { SlashCommandItem } from '../components/contract-editor/extensions/slashCommandItems';

const CATEGORY_LABELS: Record<string, string> = {
  cliente: 'Cliente',
  veiculo: 'Veículo',
  venda: 'Venda',
  compra: 'Compra',
  consignacao: 'Consignação',
  pagamento: 'Pagamento',
  reserva: 'Reserva',
  loja: 'Loja',
  rede: 'Rede',
  sistema: 'Sistema',
  manual: 'Manual',
  conjuge: 'Cônjuge',
  troca: 'Veículo de Troca',
  vendedor: 'Vendedor',
  data: 'Datas',
  garantia: 'Garantias',
  testemunha: 'Testemunhas',
  texto_dinamico: 'Textos Dinâmicos',
};

/**
 * Componente "headless" que mantém o cache module-level de itens dinâmicos
 * do slash menu sincronizado com a biblioteca de placeholders (contract_placeholder_library)
 * e os Textos Dinâmicos da rede atual.
 */
export const DynamicSlashItemsBridge: React.FC = () => {
  const { networkId } = useSessionContext();

  const { data } = useQuery({
    queryKey: ['slash-dynamic-items-bridge', networkId],
    enabled: !!networkId,
    queryFn: async () => {
      // 1) Biblioteca completa de placeholders
      const { data: library, error: eLib } = await supabase
        .from('contract_placeholder_library')
        .select('key, label, category');
      if (eLib) throw eLib;

      const libraryItems: SlashCommandItem[] = (library ?? []).map((p: any) => {
        const cat = (p.category || 'manual') as string;
        return {
          key: p.key,
          label: p.label,
          category: cat,
          categoryLabel: CATEGORY_LABELS[cat] || cat,
        };
      });

      // 2) Textos dinâmicos com placeholder_key
      const { data: defaults, error: e1 } = await supabase
        .from('default_contract_texts' as any)
        .select('id, label, placeholder_key, is_active')
        .eq('is_active', true)
        .not('placeholder_key', 'is', null);
      if (e1) throw e1;

      const { data: overrides } = await supabase
        .from('contract_texts' as any)
        .select('default_text_id, is_active_override')
        .eq('network_id', networkId);

      const overrideMap = new Map<string, any>();
      (overrides ?? []).forEach((o: any) => overrideMap.set(o.default_text_id, o));

      const dynamicItems: SlashCommandItem[] = ((defaults ?? []) as any[])
        .filter((d) => {
          const o = overrideMap.get(d.id);
          const overrideActive = o?.is_active_override;
          return overrideActive === null || overrideActive === undefined
            ? true
            : overrideActive;
        })
        .map((d) => ({
          key: d.placeholder_key,
          label: d.label,
          category: 'texto_dinamico',
          categoryLabel: 'Textos Dinâmicos',
        }));

      return [...libraryItems, ...dynamicItems];
    },
  });

  useEffect(() => {
    setDynamicSlashItems(data ?? []);
  }, [data]);

  return null;
};

export default DynamicSlashItemsBridge;
